import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { Users, TrendingUp, MessageSquare, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const tribes = [
  {
    id: 1,
    name: "Smallcap Hunters",
    members: "12K",
    description: "Daily discussions on emerging companies with high growth potential",
    trending: "KPIT Tech",
    color: "#10B981",
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=400"
  },
  {
    id: 2,
    name: "Value Investors India",
    members: "18K",
    description: "Warren Buffett style investing for the Indian market",
    trending: "ITC Analysis",
    color: "#2563EB",
    image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&q=80&w=400"
  },
  {
    id: 3,
    name: "Swing Traders Hub",
    members: "9.5K",
    description: "Short-term trading opportunities and technical setups",
    trending: "Nifty Levels",
    color: "#22D3EE",
    image: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&q=80&w=400"
  },
  {
    id: 4,
    name: "Dividend Champions",
    members: "6K",
    description: "High dividend yield stocks and passive income strategies",
    trending: "Coal India",
    color: "#F59E0B",
    image: "https://images.unsplash.com/photo-1579532537598-459ecdaf39cc?auto=format&fit=crop&q=80&w=400"
  },
  {
    id: 5,
    name: "IPO Watch",
    members: "14K",
    description: "Latest IPO analysis, grey market premium, and listing predictions",
    trending: "Upcoming IPOs",
    color: "#8B5CF6",
    image: "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?auto=format&fit=crop&q=80&w=400"
  }
];

const TribeCard = ({ tribe }) => {
  const handleJoin = () => {
    toast.success(`Joining ${tribe.name}!`, {
      description: "Coming Soon - You'll be part of this community"
    });
  };

  return (
    <motion.div
      whileHover={{ y: -8, transition: { duration: 0.3 } }}
      className="flex-shrink-0 w-[320px] bg-gradient-to-b from-[#1E293B] to-[#0F172A] rounded-2xl border border-[#334155] overflow-hidden"
      data-testid={`tribe-card-${tribe.id}`}
    >
      {/* Tribe Banner */}
      <div className="relative h-40 overflow-hidden">
        <img 
          src={tribe.image} 
          alt={tribe.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/50 to-transparent" />
        
        {/* Trending Badge */}
        <div className="absolute bottom-3 left-3 flex items-center gap-2">
          <span className="px-3 py-1 bg-[#0F172A]/80 backdrop-blur-sm rounded-full flex items-center gap-2 border border-[#334155]">
            <TrendingUp size={12} className="text-[#10B981]" />
            <span className="text-[#F8FAFC] text-xs">{tribe.trending}</span>
          </span>
        </div>

        {/* Color indicator */}
        <div 
          className="absolute top-3 right-3 w-3 h-3 rounded-full"
          style={{ backgroundColor: tribe.color }}
        />
      </div>

      {/* Tribe Info */}
      <div className="p-5">
        <h3 className="text-[#F8FAFC] font-bold text-lg mb-2">{tribe.name}</h3>
        <p className="text-[#94A3B8] text-sm mb-4 line-clamp-2">
          {tribe.description}
        </p>

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users size={14} className="text-[#94A3B8]" />
            <span className="text-[#F8FAFC] font-medium">{tribe.members}</span>
            <span className="text-[#94A3B8] text-sm">members</span>
          </div>
          <div className="flex items-center gap-1">
            <MessageSquare size={12} className="text-[#22D3EE]" />
            <span className="text-[#94A3B8] text-xs">Active</span>
          </div>
        </div>

        <button
          onClick={handleJoin}
          className="w-full py-2.5 bg-transparent border border-[#334155] hover:border-[#2563EB] hover:bg-[#2563EB]/10 text-[#F8FAFC] rounded-xl text-sm font-medium transition-all"
        >
          Join Tribe
        </button>
      </div>
    </motion.div>
  );
};

const TribeDiscovery = () => {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 340;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="tribes" className="py-20 md:py-32 relative bg-[#0a1120]" data-testid="tribe-discovery-section">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col md:flex-row md:items-end justify-between mb-12"
        >
          <div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#F8FAFC] mb-4">
              Join Your{' '}
              <span className="gradient-text">Investment Tribe</span>
            </h2>
            <p className="text-[#94A3B8] text-base md:text-lg max-w-xl">
              Find communities of like-minded investors. Discuss strategies, share insights, grow together.
            </p>
          </div>

          {/* Navigation Arrows */}
          <div className="flex gap-3 mt-6 md:mt-0">
            <button
              onClick={() => scroll('left')}
              className="w-12 h-12 rounded-full border border-[#334155] hover:border-[#2563EB] flex items-center justify-center text-[#94A3B8] hover:text-[#F8FAFC] transition-all"
              data-testid="tribes-scroll-left"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => scroll('right')}
              className="w-12 h-12 rounded-full border border-[#334155] hover:border-[#2563EB] flex items-center justify-center text-[#94A3B8] hover:text-[#F8FAFC] transition-all"
              data-testid="tribes-scroll-right"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </motion.div>
      </div>

      {/* Horizontal Scrolling Cards */}
      <div className="relative">
        {/* Gradient overlays */}
        <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-[#0a1120] to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-[#0a1120] to-transparent z-10 pointer-events-none" />

        <div 
          ref={scrollRef}
          className="flex gap-6 overflow-x-auto pb-4 px-6 md:px-[calc((100vw-1280px)/2+24px)] hide-scrollbar scroll-smooth"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {tribes.map((tribe) => (
            <TribeCard key={tribe.id} tribe={tribe} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TribeDiscovery;
